<!--
  The builder. Two columns on a wide screen: the editor, and the SAME renderer the patient sees.

  Everything about "this is a version, not a save" is in the header and the publish button's label.
  A practice that has just edited a published form is told, before it commits, that the version its
  patients already hold is not being changed.
-->
<div class="cf-builder" *ngIf="builderForm">

  <!-- ── Header: what publishing will do ───────────────────────────────────────────────────── -->
  <div class="cf-builder__header">
    <div class="cf-builder__header-text">
      <h3>Questions</h3>
      <p class="cf-builder__version-note" *ngIf="form?.currentVersionId">
        This form is published. Editing here does <strong>not</strong> change the version patients
        already have - publishing writes a <strong>new version</strong> and makes it the one
        new patients receive.
      </p>
      <p class="cf-builder__version-note" *ngIf="form && !form.currentVersionId">
        This form has never been published. Publishing creates <strong>version 1</strong>; until
        then it is a draft and nothing is sent to anybody.
      </p>
    </div>

    <div class="cf-builder__header-actions">
      <button mat-stroked-button type="button" (click)="togglePreview()">
        <mat-icon>{{ showPreview ? 'visibility_off' : 'visibility' }}</mat-icon>
        {{ showPreview ? 'Hide preview' : 'Show preview' }}
      </button>
      <button mat-flat-button color="primary" type="button"
              [disabled]="isPublishing || !form"
              (click)="publish()">
        <mat-icon>publish</mat-icon>
        {{ nextVersionLabel }}
      </button>
    </div>
  </div>

  <!-- ══════════════════════════════════════════════════════════════════════════════════════════
       THESE QUESTIONS CAME OUT OF A PDF (§16.2 rule 1)
       ══════════════════════════════════════════════════════════════════════════════════════ -->
  <div class="cf-alert cf-alert--draft" *ngIf="importedDraft" data-test="imported-draft-banner">
    <mat-icon>rate_review</mat-icon>
    <div>
      <strong>
        Unreviewed draft - read out of &ldquo;{{ importedDraft.sourceFileName }}&rdquo; by AI,
        and not published.
      </strong>
      <p>
        Every question below was guessed from the document by a machine
        <span *ngIf="importedDraft.pageCount">
          ({{ importedDraft.pageCount }} page(s) read)</span>. <strong>No person has checked
        them.</strong> Extractions drop pages, mis-read tick boxes and invent answer options.
        Nothing here is sent to any patient, and nothing becomes a version, until
        <strong>you</strong> press Publish below.
      </p>
      <p *ngIf="importedDraft.sourceFileUrl">
        <a [href]="importedDraft.sourceFileUrl" target="_blank" rel="noopener"
           data-test="imported-source-link">
          <mat-icon>open_in_new</mat-icon> Open the original PDF
        </a>
        and check the questions against it, question by question.
      </p>
    </div>
  </div>

  <!-- The extraction's own stored `validateSchema` verdict (§16.2 rule 2). -->
  <div class="cf-alert cf-alert--error"
       *ngIf="importedDraft && importGeneralErrors.length > 0"
       data-test="import-general-errors">
    <mat-icon>rule</mat-icon>
    <div>
      <strong>The extracted draft has problems that must be fixed before it can be published.</strong>
      <ul class="cf-alert__list">
        <li *ngFor="let error of importGeneralErrors; trackBy: trackByIndex">{{ error }}</li>
      </ul>
    </div>
  </div>

  <!-- ── Reorder warning — attached to the drop that caused it ─────────────────────────────── -->
  <div class="cf-alert cf-alert--warn" *ngIf="reorderWarning">
    <mat-icon>swap_vert</mat-icon>
    <div>
      <strong>{{ reorderWarning }}</strong>
      <ul class="cf-alert__list">
        <li *ngFor="let broken of brokenConditions; trackBy: trackByIndex">
          &ldquo;{{ broken.questionLabel }}&rdquo; - {{ broken.message }}
        </li>
      </ul>
    </div>
  </div>

  <!-- Conditions broken by something other than a drop (an edit, a deletion). -->
  <div class="cf-alert cf-alert--warn"
       *ngIf="!reorderWarning && brokenConditions.length > 0">
    <mat-icon>rule</mat-icon>
    <div>
      <strong>{{ brokenConditions.length }} display condition(s) cannot work as written.</strong>
      <ul class="cf-alert__list">
        <li *ngFor="let broken of brokenConditions; trackBy: trackByIndex">
          &ldquo;{{ broken.questionLabel }}&rdquo; - {{ broken.message }}
        </li>
      </ul>
    </div>
  </div>

  <!-- ── The server's refusal. Never hidden by a local pass. ───────────────────────────────── -->
  <div class="cf-alert cf-alert--error" *ngIf="serverErrors.length > 0" data-test="server-errors">
    <mat-icon>gpp_maybe</mat-icon>
    <div>
      <strong>The server refused this form.</strong>
      <ul class="cf-alert__list">
        <li *ngFor="let error of serverErrors; trackBy: trackByIndex">{{ error }}</li>
      </ul>
    </div>
  </div>

  <div class="cf-alert cf-alert--error" *ngIf="publishError" data-test="publish-error">
    <mat-icon>error_outline</mat-icon>
    <div>{{ publishError }}</div>
  </div>

  <!-- Problems that name no single question. -->
  <div class="cf-alert cf-alert--error" *ngIf="generalErrors.length > 0" data-test="general-errors">
    <mat-icon>report_problem</mat-icon>
    <div>
      <ul class="cf-alert__list">
        <li *ngFor="let error of generalErrors; trackBy: trackByIndex">{{ error }}</li>
      </ul>
    </div>
  </div>

  <div class="cf-builder__body" [class.cf-builder__body--split]="showPreview">

    <!-- ══════════════════════════════════════════════════════════════════════════════════════
         THE EDITOR
         ══════════════════════════════════════════════════════════════════════════════════ -->
    <div class="cf-builder__editor">
      <form [formGroup]="builderForm">
        <div formArrayName="sections"
             cdkDropList
             cdkDropListLockAxis="y"
             (cdkDropListDropped)="onSectionDrop($event)">

          <div class="cf-section"
               *ngFor="let sectionControl of sections.controls; index as si; trackBy: trackByControl"
               [formGroupName]="si"
               cdkDrag
               cdkDragBoundary=".cf-builder__editor">

            <div class="cf-section__bar">
              <button mat-icon-button type="button" cdkDragHandle class="cf-drag"
                      matTooltip="Drag to reorder this section" aria-label="Reorder section">
                <mat-icon>drag_indicator</mat-icon>
              </button>

              <mat-form-field appearance="outline" class="cf-section__title">
                <mat-label>Section title</mat-label>
                <input matInput formControlName="title" placeholder="e.g. Medical history">
              </mat-form-field>

              <button mat-icon-button color="warn" type="button"
                      matTooltip="Remove this section" aria-label="Remove section"
                      (click)="removeSection(si)">
                <mat-icon>delete_outline</mat-icon>
              </button>
            </div>

            <mat-form-field appearance="outline" class="cf-full">
              <mat-label>Section description (optional)</mat-label>
              <textarea matInput formControlName="description" rows="2"
                        placeholder="Shown under the section title"></textarea>
            </mat-form-field>

            <!-- ── Questions ──────────────────────────────────────────────────────────────── -->
            <div formArrayName="questions"
                 cdkDropList
                 [id]="'question-list-' + si"
                 [cdkDropListData]="si"
                 [cdkDropListConnectedTo]="questionListIds"
                 (cdkDropListDropped)="onQuestionDrop($event, si)"
                 class="cf-questions">

              <div class="cf-question"
                   *ngFor="let questionControl of questionsIn(si).controls; index as qi; trackBy: trackByControl"
                   [formGroupName]="qi"
                   cdkDrag
                   [class.cf-question--error]="hasErrors(controlValue(questionControl, 'id'))">

                <div class="cf-question__bar">
                  <button mat-icon-button type="button" cdkDragHandle class="cf-drag"
                          matTooltip="Drag to reorder this question" aria-label="Reorder question">
                    <mat-icon>drag_indicator</mat-icon>
                  </button>

                  <span class="cf-question__index">{{ qi + 1 }}</span>

                  <mat-form-field appearance="outline" class="cf-question__type">
                    <mat-label>Type</mat-label>
                    <mat-select formControlName="type" (selectionChange)="onQuestionTypeChange(si, qi)">
                      <mat-option *ngFor="let choice of questionTypes; trackBy: trackByIndex"
                                  [value]="choice.value">
                        {{ choice.label }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>

                  <button mat-icon-button color="warn" type="button"
                          matTooltip="Remove this question" aria-label="Remove question"
                          (click)="removeQuestion(si, qi)">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>

                <mat-form-field appearance="outline" class="cf-full">
                  <mat-label>
                    {{ controlValue(questionControl, 'type') === 'statictext' ? 'Text to show' : 'Question' }}
                  </mat-label>
                  <textarea matInput formControlName="question" rows="2"
                            [placeholder]="controlValue(questionControl, 'type') === 'statictext'
                              ? 'e.g. Please answer as fully as you can.'
                              : 'e.g. Do you take blood-thinning medication?'"></textarea>
                </mat-form-field>

                <div class="cf-row">
                  <mat-form-field appearance="outline" class="cf-grow"
                                  *ngIf="usesPlaceholder(controlValue(questionControl, 'type'))">
                    <mat-label>Placeholder (optional)</mat-label>
                    <input matInput formControlName="placeholder">
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="cf-grow">
                    <mat-label>Help text (optional)</mat-label>
                    <input matInput formControlName="helpText"
                           placeholder="Shown under the question">
                  </mat-form-field>
                </div>

                <div class="cf-row" *ngIf="usesRange(controlValue(questionControl, 'type'))">
                  <mat-form-field appearance="outline" class="cf-narrow">
                    <mat-label>Minimum</mat-label>
                    <input matInput type="number" formControlName="min">
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="cf-narrow">
                    <mat-label>Maximum</mat-label>
                    <input matInput type="number" formControlName="max">
                  </mat-form-field>
                  <span class="cf-hint" *ngIf="controlValue(questionControl, 'type') === 'scale'">
                    The scale is drawn as one button per point.
                  </span>
                </div>

                <mat-checkbox formControlName="required"
                              *ngIf="canBeRequired(controlValue(questionControl, 'type'))">
                  Required - the patient cannot submit without answering
                </mat-checkbox>
                <p class="cf-hint" *ngIf="!canBeRequired(controlValue(questionControl, 'type'))">
                  Information text collects no answer, so it cannot be required.
                </p>

                <!-- ── Options ──────────────────────────────────────────────────────────── -->
                <div class="cf-options" *ngIf="usesOptions(controlValue(questionControl, 'type'))"
                     formArrayName="options">
                  <div class="cf-options__head">
                    <span>Options</span>
                    <span class="cf-hint">A score is optional. Leave every score blank for an
                      unscored form.</span>
                  </div>

                  <div class="cf-option"
                       *ngFor="let optionControl of optionsIn(si, qi).controls; index as oi; trackBy: trackByIndex"
                       [formGroupName]="oi">
                    <mat-form-field appearance="outline" class="cf-grow">
                      <mat-label>Shown to the patient</mat-label>
                      <input matInput formControlName="text" placeholder="e.g. Yes, daily">
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="cf-narrow">
                      <mat-label>Stored value</mat-label>
                      <input matInput formControlName="value" placeholder="e.g. daily">
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="cf-narrow">
                      <mat-label>Score</mat-label>
                      <input matInput type="number" formControlName="score" placeholder="—">
                    </mat-form-field>
                    <button mat-icon-button color="warn" type="button"
                            matTooltip="Remove this option" aria-label="Remove option"
                            (click)="removeOption(si, qi, oi)">
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>

                  <button mat-stroked-button type="button" (click)="addOption(si, qi)">
                    <mat-icon>add</mat-icon> Add option
                  </button>
                </div>

                <!-- ── showIf ───────────────────────────────────────────────────────────────
                     The picker offers ONLY earlier questions. See the component header: a builder
                     that offers a later one has produced a form nobody can complete.
                -->
                <div class="cf-showif" formGroupName="showIf">
                  <!--
                    Not rendered at all when there is nothing earlier to depend on, rather than
                    rendered disabled: `[disabled]` on a control bound with `formControlName` is the
                    reactive-forms anti-pattern Angular warns about in the console.
                  -->
                  <mat-checkbox formControlName="enabled"
                                *ngIf="!hasNoControllingQuestions(si, qi)"
                                (change)="onShowIfToggled(si, qi)"
                                data-test="showif-enable">
                    Only show this question when&hellip;
                  </mat-checkbox>

                  <p class="cf-hint" *ngIf="hasNoControllingQuestions(si, qi)">
                    This is the first question on the form, so there is no earlier answer to depend
                    on.
                  </p>

                  <div class="cf-row cf-showif__row"
                       *ngIf="controlValue(showIfGroup(si, qi), 'enabled')">

                    <mat-form-field appearance="outline" class="cf-grow">
                      <mat-label>Question</mat-label>
                      <mat-select formControlName="questionId" (selectionChange)="onShowIfChanged()"
                                  data-test="showif-question">
                        <mat-option *ngFor="let candidate of controllingQuestions(si, qi); trackBy: trackByIndex"
                                    [value]="candidate.id">
                          {{ candidate.label }}
                        </mat-option>
                      </mat-select>
                      <mat-hint>Only questions asked earlier can be chosen.</mat-hint>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="cf-narrow">
                      <mat-label>Condition</mat-label>
                      <mat-select formControlName="operator" (selectionChange)="onShowIfChanged()">
                        <mat-option *ngFor="let operator of operators; trackBy: trackByIndex"
                                    [value]="operator.value">
                          {{ operator.label }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>

                    <ng-container
                      *ngIf="operatorChoice(controlValue(showIfGroup(si, qi), 'operator'))?.takesValue">

                      <!-- The controlling question has options: choose from them, so a typo cannot
                           produce a condition that is never true.

                           Two separate fields rather than one with a computed `formControlName`:
                           `in` stores a LIST in `values` and everything else stores a scalar in
                           `value`, and swapping a live control's name mid-edit is a good way to
                           push a string into the array control. -->
                      <mat-form-field appearance="outline" class="cf-grow"
                                      *ngIf="showIfTargetOptions(si, qi).length > 0
                                             && controlValue(showIfGroup(si, qi), 'operator') === 'in'">
                        <mat-label>Answers</mat-label>
                        <mat-select formControlName="values" multiple
                                    (selectionChange)="onShowIfChanged()">
                          <mat-option *ngFor="let option of showIfTargetOptions(si, qi); trackBy: trackByIndex"
                                      [value]="option.value">
                            {{ option.text || option.value }}
                          </mat-option>
                        </mat-select>
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="cf-grow"
                                      *ngIf="showIfTargetOptions(si, qi).length > 0
                                             && controlValue(showIfGroup(si, qi), 'operator') !== 'in'">
                        <mat-label>Answer</mat-label>
                        <mat-select formControlName="value" (selectionChange)="onShowIfChanged()">
                          <mat-option *ngFor="let option of showIfTargetOptions(si, qi); trackBy: trackByIndex"
                                      [value]="option.value">
                            {{ option.text || option.value }}
                          </mat-option>
                        </mat-select>
                      </mat-form-field>

                      <!-- Yes / No: §13.5 fixes the answers as the strings 'true' and 'false'. -->
                      <mat-form-field appearance="outline" class="cf-grow"
                                      *ngIf="showIfTargetOptions(si, qi).length === 0
                                             && showIfTargetIsYesNo(si, qi)">
                        <mat-label>Answer</mat-label>
                        <mat-select formControlName="value" (selectionChange)="onShowIfChanged()">
                          <mat-option value="true">Yes</mat-option>
                          <mat-option value="false">No</mat-option>
                        </mat-select>
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="cf-grow"
                                      *ngIf="showIfTargetOptions(si, qi).length === 0
                                             && !showIfTargetIsYesNo(si, qi)">
                        <mat-label>Answer</mat-label>
                        <input matInput formControlName="value" (input)="onShowIfChanged()">
                        <mat-hint
                          *ngIf="controlValue(showIfGroup(si, qi), 'operator') === 'in'">
                          Separate several answers with commas.
                        </mat-hint>
                      </mat-form-field>
                    </ng-container>
                  </div>
                </div>

                <!-- ── Inline problems, against the question they belong to ────────────────── -->
                <div class="cf-question__errors"
                     *ngIf="hasErrors(controlValue(questionControl, 'id'))"
                     data-test="question-errors">
                  <div class="cf-question__error"
                       *ngIf="brokenConditionFor(controlValue(questionControl, 'id')) as broken">
                    <mat-icon>rule</mat-icon> {{ broken.message }}
                  </div>
                  <div class="cf-question__error"
                       *ngFor="let error of errorsFor(controlValue(questionControl, 'id')); trackBy: trackByIndex">
                    <mat-icon>error_outline</mat-icon> {{ error }}
                  </div>

                  <!--
                    The EXTRACTION's complaint about this question (§16.2 rule 2), labelled as such
                    and shown separately from the live one. It is the server's word, taken when the
                    draft was stored, and it disappears when this question is edited.
                  -->
                  <div class="cf-question__error cf-question__error--import"
                       *ngFor="let error of importErrorsFor(controlValue(questionControl, 'id')); trackBy: trackByIndex"
                       data-test="import-question-error">
                    <mat-icon>rate_review</mat-icon>
                    <span><em>From the PDF extraction:</em> {{ error }}</span>
                  </div>
                </div>
              </div>

              <p class="cf-empty" *ngIf="questionsIn(si).controls.length === 0">
                This section has no questions yet. A section with no questions cannot be published.
              </p>
            </div>

            <div class="cf-section__add">
              <button mat-stroked-button type="button" (click)="addQuestion(si)">
                <mat-icon>add</mat-icon> Add question
              </button>
            </div>
          </div>
        </div>
      </form>

      <button mat-stroked-button color="primary" type="button" class="cf-add-section"
              (click)="addSection()">
        <mat-icon>library_add</mat-icon> Add section
      </button>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════════════════════
         THE PREVIEW — the SAME component the patient uses. §5.1.
         ══════════════════════════════════════════════════════════════════════════════════ -->
    <div class="cf-builder__preview" *ngIf="showPreview">
      <div class="cf-preview__head">
        <div>
          <h4>Preview</h4>
          <span class="cf-hint">Exactly what the patient sees. Try it - nothing is
            saved.</span>
        </div>
        <button mat-icon-button type="button" (click)="resetPreview()"
                matTooltip="Clear the preview's answers" aria-label="Clear preview answers">
          <mat-icon>restart_alt</mat-icon>
        </button>
      </div>

      <div class="cf-preview__surface">
        <p class="cf-empty" *ngIf="previewIsEmpty">
          Add a question and it will appear here.
        </p>

        <app-dynamic-form-renderer
          *ngIf="!previewIsEmpty"
          [schema]="previewSchema"
          [value]="previewAnswers"
          (valueChange)="onPreviewValueChange($event)">
        </app-dynamic-form-renderer>
      </div>
    </div>
  </div>
</div>
